/**
 * Harta de ecrane, o singura data: bara laterala, paleta de comenzi si titlurile de pagina citesc
 * toate din lista asta. Fiecare intrare pastreaza si INTREBAREA la care raspunde ecranul —
 * criteriul prin care se decide daca un element are ce cauta acolo.
 *
 * Rutele cu parametru sunt marcate cu `fel` in loc de `to`, ca `<Link>` sa rmn verificat de
 * TypeScript (o cale ca text ar strica exact garantia pentru care am ales TanStack Router).
 */

import {
  Activity,
  Banknote,
  CalendarDays,
  ClipboardList,
  Coins,
  GitCompare,
  LayoutGrid,
  Package,
  Percent,
  ScrollText,
  Settings,
  Upload,
  Users,
  type LucideIcon,
} from 'lucide-react';

export type CaleStatica =
  | '/matrice'
  | '/cashflow'
  | '/furnizori'
  | '/stocuri'
  | '/comisioane'
  | '/activitate'
  | '/import-export'
  | '/reconciliere'
  | '/jurnal'
  | '/setari';

export type Destinatie =
  | { fel: 'static'; to: CaleStatica }
  /** `/zi/$data` — data vine din contextul global de zi. */
  | { fel: 'zi' }
  /** `/valutar/$data` — aceeasi zi. */
  | { fel: 'valutar' }
  /** `/raport/$perioada` — saptamana ISO a zilei curente. */
  | { fel: 'raport' };

export type GrupNav = 'zilnic' | 'analiza' | 'date' | 'administrare';

export const ETICHETE_GRUP: Record<GrupNav, string> = {
  zilnic: 'Bucla zilnică',
  analiza: 'Analiză',
  date: 'Date și rapoarte',
  administrare: 'Administrare',
};

export interface IntrareNav {
  cheie: string;
  eticheta: string;
  intrebare: string;
  grup: GrupNav;
  icon: LucideIcon;
  /** Cuvinte suplimentare pentru paleta de comenzi (inclusiv termenii din Excel). */
  cuvinte: string;
  dest: Destinatie;
}

export const NAVIGATIE: readonly IntrareNav[] = [
  {
    cheie: 'zi',
    eticheta: 'Plan de plăți',
    intrebare: 'Cât am azi, cui plătesc, ce marchez?',
    grup: 'zilnic',
    icon: CalendarDays,
    cuvinte: 'zi scadente plati situatie curenta runda dimineata edl mirela',
    dest: { fel: 'zi' },
  },
  {
    cheie: 'valutar',
    eticheta: 'Comparator curs',
    intrebare: 'Prin ce bancă trimit și cât economisesc?',
    grup: 'zilnic',
    icon: Coins,
    cuvinte: 'valutar curs bnr bcr raiffeisen bt ebury saving comision eur usd huf',
    dest: { fel: 'valutar' },
  },
  {
    cheie: 'matrice',
    eticheta: 'Vedere matrice',
    intrebare: 'Când l-am plătit ultima dată? Ce tipar are?',
    grup: 'analiza',
    icon: LayoutGrid,
    cuvinte: 'matrice furnizori zile grila scrubber densitate',
    dest: { fel: 'static', to: '/matrice' },
  },
  {
    cheie: 'cashflow',
    eticheta: 'Cashflow & disponibil',
    intrebare: 'De unde vin banii, ce linie am accesat?',
    grup: 'analiza',
    icon: Banknote,
    cuvinte: 'cashflow disponibil conturi linie credit plafon transferuri trezorerie casa',
    dest: { fel: 'static', to: '/cashflow' },
  },
  {
    cheie: 'furnizori',
    eticheta: 'Furnizori',
    intrebare: 'Ce sold/stoc/avans are, ce reguli, ce istoric?',
    grup: 'analiza',
    icon: Users,
    cuvinte: 'furnizori cui iban categorii termen plata limita credit alias',
    dest: { fel: 'static', to: '/furnizori' },
  },
  {
    cheie: 'stocuri',
    eticheta: 'Stoc & solduri',
    intrebare: 'Stoc > sold ⇒ nu se plătește. Cine e plătit în avans?',
    grup: 'analiza',
    icon: Package,
    cuvinte: 'stoc sold wme avans prepaid marfa nevanduta',
    dest: { fel: 'static', to: '/stocuri' },
  },
  {
    cheie: 'comisioane',
    eticheta: 'Comisioane & tarife',
    intrebare: 'Ce tarif, pe ce prag, de când — și cât am plătit efectiv?',
    grup: 'analiza',
    icon: Percent,
    cuvinte: 'comisioane tarife praguri suprataxe turcia express transferuri interne',
    dest: { fel: 'static', to: '/comisioane' },
  },
  {
    cheie: 'activitate',
    eticheta: 'Comentarii & activitate',
    intrebare: 'Ce s-a schimbat, de cine? Pe ce plată era factura X?',
    grup: 'date',
    icon: Activity,
    cuvinte: 'activitate comentarii audit facturi cautare fts glosar reguli furnizor',
    dest: { fel: 'static', to: '/activitate' },
  },
  {
    cheie: 'import-export',
    eticheta: 'Import / Export',
    intrebare: 'Am adus WME/Stoc de săptămâna asta? Scot un xlsx?',
    grup: 'date',
    icon: Upload,
    cuvinte: 'import export xlsx wme stoc lista facturi diff conflicte carantina',
    dest: { fel: 'static', to: '/import-export' },
  },
  {
    cheie: 'raport',
    eticheta: 'Raport săptămânal',
    intrebare: 'Ce raportez pentru săptămâna asta? (print-first)',
    grup: 'date',
    icon: ClipboardList,
    cuvinte: 'raport saptamanal print sablon plus minus neta contributii capital',
    dest: { fel: 'raport' },
  },
  {
    cheie: 'reconciliere',
    eticheta: 'Reconciliere',
    intrebare: 'De ce diferă cifra asta de cea din fișier?',
    grup: 'date',
    icon: GitCompare,
    cuvinte: 'reconciliere excel diferente legacy toleranta formule greșite',
    dest: { fel: 'static', to: '/reconciliere' },
  },
  {
    cheie: 'jurnal',
    eticheta: 'Jurnal',
    intrebare: 'Ce s-a întâmplat, cine a făcut-o, ce era acolo înainte?',
    grup: 'administrare',
    icon: ScrollText,
    cuvinte: 'jurnal audit stergeri sterse restaurare istoric cine cand log urma',
    dest: { fel: 'static', to: '/jurnal' },
  },
  {
    cheie: 'setari',
    eticheta: 'Setări',
    intrebare: 'Utilizatori, conturi, categorii, calendar, date de acces bănci.',
    grup: 'administrare',
    icon: Settings,
    cuvinte: 'setari utilizatori roluri conturi categorii calendar sarbatori date acces parole',
    dest: { fel: 'static', to: '/setari' },
  },
];

export const GRUPURI_NAV: readonly GrupNav[] = ['zilnic', 'analiza', 'date', 'administrare'];
